/**
 * Shared guard that keeps the floating DEAN panel from accidentally dismissing
 * Radix overlays (Popover, DropdownMenu, Select, ContextMenu, HoverCard,
 * Menubar, Dialog, Sheet) that are open on the page behind it.
 *
 * Radix DismissableLayer detects "click outside" via a capture-phase
 * `pointerdown` listener on the document. When the user clicks inside the DEAN
 * panel, the pointer target is outside the overlay's content, so Radix would
 * normally dismiss the overlay. The DEAN panel marks its root with
 * `data-dean-panel="true"`; this helper checks whether the originating event
 * came from inside that marker and, if so, calls `preventDefault()` so Radix
 * cancels the dismissal. The caller's own handler is chained otherwise.
 *
 * Using the Radix-supported `onPointerDownOutside` / `onInteractOutside`
 * callbacks (rather than intercepting the capture-phase listener ourselves)
 * avoids breaking DEAN's own drag/resize handlers.
 */

const DEAN_PANEL_SELECTOR = "[data-dean-panel]";

type DismissableEvent = {
  detail: { originalEvent: Event };
  preventDefault: () => void;
};

export function isEventInsideDeanPanel(e: DismissableEvent): boolean {
  const target = e.detail?.originalEvent?.target as Element | null;
  return Boolean(target?.closest?.(DEAN_PANEL_SELECTOR));
}

/**
 * Wraps a Radix `onPointerDownOutside` / `onInteractOutside` handler so that
 * interactions originating inside the DEAN panel never dismiss the overlay.
 */
export function guardDeanPanelDismiss<E extends DismissableEvent>(
  handler?: (e: E) => void,
): (e: E) => void {
  return (e: E) => {
    if (isEventInsideDeanPanel(e)) {
      e.preventDefault();
      return;
    }
    handler?.(e);
  };
}
